// src/components/RaportONas.jsx

import React, { useRef, useState, useEffect } from "react";
import { Search, Loader2 } from "lucide-react";

// krsApi
import { fetchCurrentExtract } from "./api/krs/krsApi";

// Transparent Data "kontreport" building blocks
import { ResponsiveStyles } from "./api/transparentdata/kontreport/atoms";
import StatusMonitoringSection from "./api/transparentdata/kontreport/StatusMonitoringSection";
import DanePodstawoweSection from "./api/transparentdata/kontreport/DanePodstawoweSection";
import DaneAdresoweSection from "./api/transparentdata/kontreport/DaneAdresoweSection";
import DaneKRSSection from "./api/transparentdata/kontreport/DaneKRSSection";
import KnfListSection from "./api/transparentdata/kontreport/KnfListSection";
import PublicDebtSection from "./api/transparentdata/kontreport/PublicDebtSection";
import UreLicensesSection from "./api/transparentdata/kontreport/UreLicensesSection";
import WhiteListSection from "./api/transparentdata/kontreport/WhiteListSection";
import EuGrantsSection from "./api/transparentdata/kontreport/EuGrantsSection";
import ContactDataSection from "./api/transparentdata/kontreport/ContactDataSection";
import BoardDataSection from "./api/transparentdata/kontreport/BoardDataSection";
import OwnersDataSection from "./api/transparentdata/kontreport/OwnersDataSection";
import FinancialDataSection from "./api/transparentdata/kontreport/FinancialDataSection";
import DebtsDataSection from "./api/transparentdata/kontreport/DebtsDataSection";
import WzmiankiDokumentySection from "./api/transparentdata/kontreport/WzmiankiDokumentySection";
import RelationsSection from "./api/transparentdata/kontreport/RelationsSection";
import PkdSection from "./api/transparentdata/kontreport/PkdSection";

// Transparent Data client functions
import {
  fetchPrzeswietlReport,
  fetchRiskReport,
  fetchRelationsReport,
} from "./api/transparentdata/tdClient";

// Export toolbar for PDF/PNG etc.
import ExportToolbar from "./api/transparentdata/kontreport/ExportToolbar";

/** Simple inline Title Separator */
const TitleSeparator = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      gap: 12,
      margin: "8px 0 4px",
    }}
  >
    <div style={{ height: 1, background: "#e5e7eb" }} />
    <div
      style={{
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: 0.6,
        color: "#6b7280",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
    <div style={{ height: 1, background: "#e5e7eb" }} />
  </div>
);

// ---------- helpers ----------
function decodeJwtPayload(t) {
  try {
    return JSON.parse(atob(t.split(".")[1]));
  } catch {
    return {};
  }
}

// pick-only relations fields for snapshot schema
function pickRelationsForSchema(rel) {
  if (!rel || typeof rel !== "object") return null;
  const src = rel.json && typeof rel.json === "object" ? rel.json : rel;
  const out = {};
  if (src.company) out.company = src.company;
  if (src.relations) out.relations = src.relations;
  if (src.graph) out.graph = src.graph;
  return Object.keys(out).length ? out : null;
}

const REL_DEPTH = 2;

function buildSnapshotPayload({
  nip,
  REL_DEPTH,
  apiResult,
  riskResult,
  relationsResult,
  krsOdpisRaw,
  krsEmails,
  krs,
  knf,
  publicDebt,
  ure,
  white,
  euGrants,
  partners,
  reporter,
}) {
  return {
    meta: {
      generatedAt: new Date().toISOString(),
      lastUpdate: new Date().toISOString(),
      reportGeneratedBy: reporter ? [String(reporter)] : [],
      nip: String(nip || "").replace(/\D/g, ""),
      relDepth: REL_DEPTH,
    },
    sources: {
      przeszwietl: { result: apiResult || null, error: null },
      risk: { result: riskResult || null, error: null },
      relations: { result: pickRelationsForSchema(relationsResult), error: null },
      krsExtract: { raw: krsOdpisRaw || null },
    },
    derived: {
      emailsFromKrs: krsEmails || [],
      krsResolved: krs || null,
      knf: knf || [],
      publicDebt: publicDebt || [],
      ure: ure || [],
      whiteList: white || null,
      euGrants: euGrants || [],
      partners: partners || [],
    },
  };
}

async function postCompanySnapshot({ token, payload }) {
  const res = await fetch("https://server229256.nazwa.pl/api/company-snapshots", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg += ` - ${JSON.stringify(await res.json())}`;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

async function getLatestSnapshot({ token, nip }) {
  const res = await fetch(
    `https://server229256.nazwa.pl/api/company-snapshots/${nip}/latest`,
    {
      headers: { Authorization: token },
    }
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg += ` - ${JSON.stringify(await res.json())}`;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// ---------- PURE REPORT COMPONENT (no layout) ----------
export function RaportONasInner() {
  const reportRef = useRef(null);

  const [nip, setNip] = useState("");
  const [autoFetched, setAutoFetched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [apiResult, setApiResult] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [riskErr, setRiskErr] = useState("");
  const [krsEmails, setKrsEmails] = useState([]);
  const [krsOdpisRaw, setKrsOdpisRaw] = useState(null);

  const [relationsResult, setRelationsResult] = useState(null);
  const [relationsErr, setRelationsErr] = useState("");

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const storedUser =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const email = storedUser ? JSON.parse(storedUser)?.Email : null;

  // 1) Load NIP from InvoiceSettings backend
  useEffect(() => {
    if (!email) return;
    let cancelled = false;

    const fetchInvoiceData = async () => {
      try {
        const res = await fetch(
          `https://server229256.nazwa.pl/api/invoice/${email}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.nip) {
          setNip(String(data.nip));
        }
      } catch (e) {
        console.warn(
          "⚠️ Nie udało się pobrać danych faktury / NIP:",
          e?.message || e
        );
      }
    };

    fetchInvoiceData();
    return () => {
      cancelled = true;
    };
  }, [email]);

  // ---------- main fetch ----------
  const handleFetch = async () => {
    if (!token) {
      setErr(
        "Brak tokenu. Zaloguj się lub ustaw token w localStorage pod kluczem 'token'."
      );
      return;
    }

    const clean = (nip || "").replace(/\D/g, "");
    if (!/^\d{10}$/.test(clean)) {
      setErr(
        "Niepoprawny NIP w ustawieniach. Przejdź do Ustawienia → Dane do Faktury i uzupełnij NIP (10 cyfr)."
      );
      return;
    }

    setLoading(true);
    setErr("");
    setRiskErr("");
    setRelationsErr("");
    setApiResult(null);
    setRiskResult(null);
    setRelationsResult(null);
    setKrsEmails([]);
    setKrsOdpisRaw(null);

    const get = (obj, path, dflt = undefined) =>
      path
        .split(".")
        .reduce((o, k) => (o && o[k] !== undefined ? o[k] : dflt), obj);

    const preview = (obj, max = 5) => {
      if (!obj || typeof obj !== "object") return obj;
      const keys = Object.keys(obj).slice(0, max);
      return keys.reduce((acc, k) => ((acc[k] = obj[k]), acc), {});
    };

    const isStale = (snapshot, maxDays = 15) => {
      const ts = get(snapshot, "meta.lastUpdate") || get(snapshot, "meta.generatedAt");
      if (!ts) return true;
      const ageMs = Date.now() - new Date(ts).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      console.info(
        `📅 Snapshot age: ${ageDays.toFixed(1)} days (threshold ${maxDays})`
      );
      return ageDays > maxDays;
    };

    const extractEmailsFromD1 = (d1) => {
      const node = get(d1, "siedzibaIAdres.adresPocztyElektronicznej");
      const asArr = Array.isArray(node) ? node : node ? [node] : [];
      return [
        ...new Set(
          asArr
            .flatMap((x) =>
              typeof x === "string"
                ? [x]
                : [
                    x?.adresPocztyElektronicznej,
                    x?.email,
                    x?.value,
                  ].filter(Boolean)
            )
            .map((s) => String(s).trim())
        ),
      ].filter(Boolean);
    };

    const fetchEmailsViaKrsClient = async (krsNumber) => {
      console.groupCollapsed("[KRS odpis] via krsApi", krsNumber);
      try {
        const raw = await fetchCurrentExtract(krsNumber);
        setKrsOdpisRaw(raw);
        const d1 = get(raw, "odpis.dane.dzial1", null);
        console.log("d1 keys:", d1 && Object.keys(d1).slice(0, 5));
        const emails = extractEmailsFromD1(d1);
        console.log("emails:", emails);
        return emails;
      } catch (e) {
        console.error("krsApi error:", e);
        return [];
      } finally {
        console.groupEnd();
      }
    };

    const started = performance.now();

    try {
      // 0) CACHE FIRST
      try {
        console.log(`🔎 Checking cache (Mongo) for NIP: ${clean}`);
        const cached = await getLatestSnapshot({ token, nip: clean });
        if (cached && !isStale(cached, 15)) {
          console.info("📦 DATA SOURCE: CACHE (Mongo) — fresh (≤15 days)");
          const przRes = cached?.sources?.przeszwietl?.result || null;
          const riskRes = cached?.sources?.risk?.result || null;
          const relRes = cached?.sources?.relations?.result || null;
          const odpisRaw = cached?.sources?.krsExtract?.raw || null;
          const emailsK = cached?.derived?.emailsFromKrs || [];

          if (przRes?.krs && typeof przRes.krs === "object") {
            przRes.krs = {
              ...przRes.krs,
              name: (przRes.krs.name || "").replace(/^"+|"+$/g, ""),
            };
          }

          setApiResult(przRes);
          setRiskResult(riskRes);
          setRelationsResult(relRes);
          setKrsOdpisRaw(odpisRaw);
          setKrsEmails(emailsK);

          console.log(
            `⏱️ Loaded from CACHE (elapsed: ${Math.round(
              performance.now() - started
            )} ms)`
          );
          setLoading(false);
          return;
        } else if (cached) {
          console.info(
            "🟡 DATA SOURCE: CACHE is STALE (>15 days) — refreshing from vendor APIs…"
          );
        } else {
          console.info("🚫 Cache miss — no snapshot found for this NIP");
        }
      } catch (cacheErr) {
        console.warn(
          "Cache lookup failed — will call vendor APIs:",
          cacheErr?.message || cacheErr
        );
      }

      // 1) Vendor APIs
      console.groupCollapsed("[Combined fetch] start — DATA SOURCE: VENDOR APIs");
      const [prz, risk, rel] = await Promise.allSettled([
        fetchPrzeswietlReport({ nip: clean, token }),
        fetchRiskReport({ nip: clean, token }),
        fetchRelationsReport
          ? fetchRelationsReport({ nip: clean, depth: REL_DEPTH, token })
          : Promise.resolve({ result: null }),
      ]);

      // --- Prześwietl ---
      let krsNumber = null;
      let przFirst = null;
      if (prz.status === "fulfilled") {
        const data = prz.value;
        przFirst = data?.result || data?.data?.[0]?.result || null;
        console.log("[Prześwietl] first (preview):", preview(przFirst));
        const krsRaw = przFirst?.krs;
        if (krsRaw && typeof krsRaw === "object") {
          przFirst.krs = {
            ...krsRaw,
            name: (krsRaw.name || "").replace(/^"+|"+$/g, ""),
          };
        }
        setApiResult(przFirst);

        krsNumber =
          przFirst?.krs?.krs ||
          przFirst?.krs?.krs_number ||
          przFirst?.krs?.numerKRS ||
          przFirst?.krs;
        console.log("[Prześwietl] resolved krsNumber =", krsNumber);
      } else {
        const msg =
          prz.reason?.message || "Nie udało się pobrać raportu (Prześwietl).";
        console.warn("[Prześwietl] error:", prz.reason);
        setErr(msg);
      }

      // --- Risk ---
      let riskFirst = null;
      if (risk.status === "fulfilled") {
        const rdata = risk.value;
        riskFirst = rdata?.result || rdata?.data?.[0]?.result || null;
        console.log("[Risk] first (preview):", preview(riskFirst));
        setRiskResult(riskFirst);
      } else {
        const msg =
          risk.reason?.message || "Nie udało się pobrać raportu ryzyka.";
        console.warn("[Risk] error:", risk.reason);
        setRiskErr(msg);
      }

      // --- Relations ---
      let relJson = null;
      if (fetchRelationsReport) {
        if (rel.status === "fulfilled") {
          const rdata = rel.value?.result || null;
          relJson = rdata?.json || null;
          console.log("[Relations] json (preview):", preview(relJson));
          setRelationsResult(relJson);
        } else {
          const msg =
            rel.reason?.message || "Nie udało się pobrać raportu powiązań.";
          console.warn("[Relations] error:", rel.reason);
          setRelationsErr(msg);
        }
      }

      // 2) KRS → e-maile
      const emails = krsNumber ? await fetchEmailsViaKrsClient(krsNumber) : [];
      setKrsEmails(emails);

      // 3) save snapshot
      const przOk = prz.status === "fulfilled";
      const riskOk = risk.status === "fulfilled";
      const relOk = fetchRelationsReport ? rel.status === "fulfilled" : false;

      if (!przOk && !riskOk && !relOk) {
        throw new Error("Nie udało się pobrać żadnego z raportów.");
      }

      try {
        const jwtData = decodeJwtPayload(token);
        const reporter = jwtData?.Company || jwtData?.company || "unknown";

        const krsObj =
          przFirst?.krs && typeof przFirst.krs === "object"
            ? { ...przFirst.krs }
            : null;

        const knf = Array.isArray(przFirst?.knf) ? przFirst.knf : [];
        const publicDebt = Array.isArray(przFirst?.public_debt)
          ? przFirst.public_debt
          : [];
        const ure = Array.isArray(przFirst?.ure) ? przFirst.ure : [];
        const white = przFirst?.white_list || null;

        const euGrants =
          (Array.isArray(przFirst?.eu_grants) && przFirst.eu_grants.length
            ? przFirst.eu_grants
            : Array.isArray(white?.eu_grants)
            ? white.eu_grants
            : []) || [];

        const partnersTop = Array.isArray(przFirst?.partners)
          ? przFirst.partners
          : [];
        const partnersWhite = Array.isArray(
          white?.relations?.partners
        )
          ? white.relations.partners
          : [];
        const partners = [...partnersTop, ...partnersWhite];

        const snapshotPayload = buildSnapshotPayload({
          nip: clean,
          REL_DEPTH,
          apiResult: przFirst || null,
          riskResult: riskFirst || null,
          relationsResult: relOk ? rel.value?.result || null : null,
          krsOdpisRaw,
          krsEmails: emails,
          krs: krsObj,
          knf,
          publicDebt,
          ure,
          white,
          euGrants,
          partners,
          reporter,
        });

        await postCompanySnapshot({ token, payload: snapshotPayload });
        console.info("✅ Snapshot saved (refresh).");
      } catch (autoErr) {
        console.warn("⚠️ Auto-save failed:", autoErr);
      }

      console.log(
        `⏱️ Loaded from VENDOR APIs (elapsed: ${Math.round(
          performance.now() - started
        )} ms)`
      );
      console.groupEnd();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Nie udało się pobrać raportu.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch once when NIP from settings becomes valid
  useEffect(() => {
    const clean = (nip || "").replace(/\D/g, "");
    if (!autoFetched && clean.length === 10) {
      handleFetch();
      setAutoFetched(true);
    }
  }, [nip, autoFetched]); // intentionally not adding handleFetch

  // --- Map ONLY API data to sections ---
  const krsRaw = apiResult?.krs || null;
  const krs =
    krsRaw && typeof krsRaw === "object"
      ? { ...krsRaw, name: (krsRaw.name || "").replace(/^"+|"+$/g, "") }
      : null;

  const knf = Array.isArray(apiResult?.knf) ? apiResult.knf : [];
  const publicDebt = Array.isArray(apiResult?.public_debt)
    ? apiResult.public_debt
    : [];
  const ure = Array.isArray(apiResult?.ure) ? apiResult.ure : [];
  const white = apiResult?.white_list || null;

  const euGrants =
    (Array.isArray(apiResult?.eu_grants) && apiResult.eu_grants.length
      ? apiResult.eu_grants
      : Array.isArray(white?.eu_grants)
      ? white.eu_grants
      : []) || [];

  const partnersTop = Array.isArray(apiResult?.partners)
    ? apiResult.partners
    : [];
  const partnersWhite = Array.isArray(white?.relations?.partners)
    ? white.relations.partners
    : [];
  const partners = [...partnersTop, ...partnersWhite];

  const riskContact = riskResult?.contact_data || {};
  const riskBoard = riskResult?.board_data || {};
  const riskOwners = riskResult?.owners_data || {};
  const riskFinancial = riskResult?.financial_data || {};
  const riskDebts = riskResult?.debts_data || {};

  const hasReport = !!(apiResult || riskResult);

  const Header = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 8px",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.3 }}>
        Raport o nas
      </div>
      <div style={{ fontSize: 11, color: "#6b7280" }}>
        {new Date().toLocaleDateString("pl-PL")}
      </div>
    </div>
  );
  const Footer = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 8px",
        fontSize: 10,
        color: "#9ca3af",
      }}
    >
      <span>Źródło: Transparent Data</span>
      <span>{"Strona {{page}} / {{pages}}"}</span>
    </div>
  );

  return (
    <>
      {/* Top bar – info about fixed NIP + refresh + export */}
      <div
        style={{
          border: "1px solid #e6e9ee",
          borderRadius: 16,
          padding: 12,
          background: "#fff",
          boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "grid",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr auto auto",
            gap: 10,
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              NIP z ustawień faktury
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {nip ? nip : "Brak NIP w ustawieniach"}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
              Aby zmienić NIP, przejdź do sekcji{" "}
              <strong>Ustawienia / Dane do Faktury</strong>.
            </div>
          </div>
{loading && (
  <button
    // button is only rendered when loading === true
    style={{
      padding: "0 16px",
      minWidth: 180,
      borderRadius: 12,
      border: "1px solid #0b74e5",
      background: "#bcd7fb",
      color: "#fff",
      fontSize: 15,
      fontWeight: 600,
      cursor: "not-allowed",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      height: 42,
    }}
  >
    <Loader2
      size={18}
      style={{ animation: "spin 1s linear infinite" }}
    />
  </button>
)}



          {hasReport ? (
            <div
              className="export-toolbar-wrap"
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <ExportToolbar
                targetRef={reportRef}
                filename="raport-o-nas"
                header={Header}
                footer={Footer}
              />
            </div>
          ) : (
            <div />
          )}
        </div>

        {err ? (
          <div style={{ color: "#b00020", fontSize: 14 }}>{err}</div>
        ) : null}
        {riskErr ? (
          <div style={{ color: "#b00020", fontSize: 12 }}>{riskErr}</div>
        ) : null}
        {relationsErr ? (
          <div style={{ color: "#b00020", fontSize: 12 }}>{relationsErr}</div>
        ) : null}
      </div>

      {/* Report */}
      {hasReport ? (
        <div
          ref={reportRef}
          style={{
            display: "grid",
            gap: 12,
            background: "#fff",
            padding: 12,
          }}
        >
          <ResponsiveStyles />

          {/* Core sections fed by /przeswietl */}
          <div data-export-chunk>
            <DanePodstawoweSection krs={krs} />
          </div>
          <div data-export-chunk>
            <StatusMonitoringSection
              krs={krs}
              white={white}
              risk={riskResult}
            />
          </div>
          <div data-export-chunk>
            <ContactDataSection data={riskContact} emailsKrs={krsEmails} />
          </div>
          <div data-export-chunk>
            <DaneAdresoweSection address={krs?.address || null} />
          </div>
          <div data-export-chunk>
            <DaneKRSSection krs={krs} />
          </div>
          <div data-export-chunk>
            <PkdSection rawOdpis={krsOdpisRaw} />
          </div>
          <div data-export-chunk>
            <BoardDataSection data={riskBoard} />
          </div>
          <div data-export-chunk>
            <OwnersDataSection data={riskOwners} />
          </div>
          <div data-export-chunk>
            <FinancialDataSection data={riskFinancial} />
          </div>
          <div data-export-chunk>
            <DebtsDataSection data={riskDebts} />
          </div>

          {/* Risk sections fed by /risk */}
          <div data-export-chunk>
            <TitleSeparator>
              Informacje podstawowe — Raport ryzyka
            </TitleSeparator>
          </div>
          <div data-export-chunk>
            <WzmiankiDokumentySection rawOdpis={krsOdpisRaw} />
          </div>
          <div data-export-chunk>
            <KnfListSection items={knf} />
          </div>
          <div data-export-chunk>
            <PublicDebtSection items={publicDebt} />
          </div>
          <div data-export-chunk>
            <UreLicensesSection items={ure} />
          </div>
          <div data-export-chunk>
            <EuGrantsSection items={euGrants} />
          </div>
          <div data-export-chunk>
            <WhiteListSection data={white} />
          </div>

          <div data-export-chunk>
            <TitleSeparator>
              Informacje organizacyjno/osobowych — Raport powiązań
            </TitleSeparator>
          </div>
          <div data-export-chunk>
            <RelationsSection data={relationsResult} />
          </div>
        </div>
      ) : (
        !loading && (
          <p style={{ color: "#6b7280", marginTop: 8 }}>
            Raport zostanie wygenerowany automatycznie po pobraniu NIP z
            ustawień faktury. Jeśli nic się nie dzieje, upewnij się, że w
            ustawieniach uzupełniono poprawny NIP.
          </p>
        )
      )}

      {/* force consistent dimensions for export buttons */}
      <style>{`
        .export-toolbar-wrap :where(button) {
          height: 30px;
          min-width: 160px;
          padding: 0 16px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          line-height: 1;
        }
        .export-toolbar-wrap :where(button[data-variant="pdf"], button.pdf, button[title*="PDF"]) {
          background: #dc2626;
          border: 1px solid #b91c1c;
          color: #fff;
        }
        .export-toolbar-wrap :where(button[data-variant="pdf"]:hover, button.pdf:hover, button[title*="PDF"]:hover) {
          background: #b91c1c;
        }
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
    </>
  );
}
